<!-- fallow-ignore-file complexity -->
<div
	class="card-outer-wrapper"
	[class.has-error]="hasError()"
	[class.card--selected]="isSelected()"
	[attr.data-testid]="effectiveTestId()"
	(click)="onCardClick()"
>
	<!-- Debug info -->
	@if (isDebugMode()) {
		<div
			class="debug-info"
			style="
				position: absolute;
				top: 4px;
				left: 4px;
				z-index: 100;
				background: rgba(0, 0, 0, 0.75);
				color: #fff;
				padding: 4px 8px;
				font-size: 10px;
				font-family: 'SF Mono', Monaco, monospace;
				border-radius: 4px;
				backdrop-filter: blur(4px);
				display: flex;
				gap: 8px;
				align-items: center;
			"
		>
			<span style="color: #4fc3f7">{{ cardId() }}</span>
			<span
				style="
					background: #2196f3;
					padding: 1px 6px;
					border-radius: 3px;
					font-size: 9px;
					text-transform: uppercase;
				"
			>
				{{ chartType() }}
			</span>
		</div>
	}

	<!-- Top progress bar (refresh or initial load) -->
	@if (showTopProgressBar() && !hasError()) {
		<div class="card-loader">
			<jepto-ui-progress-bar-indeterminate />
		</div>
	}

	<!-- Card header -->
	@if (effectiveTitle() || effectiveSubtitle() || effectiveIcon()) {
		<div class="card-header">
			<div class="card-icon-title-wrapper">
				@if (effectiveIcon()) {
					<mat-icon class="material-symbols-outlined card-icon">{{
						effectiveIcon()
					}}</mat-icon>
				}
				<div class="card-title-row">
					@if (effectiveTitle()) {
						<h2 class="card-title">{{ effectiveTitle() }}</h2>
					}
					<ng-content select="[data-chart-title-addon]" />
				</div>
			</div>
			@if (effectiveSubtitle()) {
				<div class="card-subtitle">{{ effectiveSubtitle() }}</div>
			}
		</div>
	}

	<!-- Error state -->
	@if (hasError()) {
		<jepto-card-query-status
			kind="error"
			noun="chart"
			[chartType]="chartType()"
			[testId]="effectiveTestId() + '-query-error'"
			(refresh)="onRetry()"
		/>
	}

	<!-- Chart content -->
	@if (!isBlockingLoad() && !hasError()) {
		<div class="card-body">
			@if (showCreateChartEmptyState()) {
				<div class="card-create-empty-state">
					<div class="card-create-empty-state__title">
						{{ createChartEmptyStateTitle() }}
					</div>
					<div class="card-create-empty-state__line">
						Pick a dimension and metric under Settings &gt; Data to
						build your chart.
					</div>
					<div class="card-create-empty-state__line">
						Or describe it and let AI do the setup.
					</div>
				</div>
			} @else if (showPageQueryEmptyState()) {
				<jepto-card-query-status
					kind="empty"
					noun="chart"
					[chartType]="chartType()"
					[testId]="effectiveTestId() + '-query-empty'"
					(refresh)="onRetry()"
				/>
			} @else {
				@if (isLineChart() && (hasValidData() || allowEmptyChart())) {
					<jepto-line-chart
						[cardId]="
							effectiveConfig()?.cardId
						"
						[config]="preparedConfig()"
						[data]="effectiveData()"
						[themeMode]="themeMode()"
						[palette]="effectivePalette()"
						[themeConfig]="themeConfig()"
						[reportThemePaletteId]="reportThemePaletteId()"
					/>
				}

				@if (isAreaChart() && (hasValidData() || allowEmptyChart())) {
					<jepto-area-chart
						[cardId]="
							effectiveConfig()?.cardId
						"
						[config]="preparedConfig()"
						[data]="effectiveData()"
						[themeMode]="themeMode()"
						[palette]="effectivePalette()"
						[themeConfig]="themeConfig()"
					/>
				}

				@if (isBarChart() && (hasValidData() || allowEmptyChart())) {
					<jepto-bar-chart
						[cardId]="
							effectiveConfig()?.cardId
						"
						[config]="preparedConfig()"
						[data]="effectiveData()"
						[themeMode]="themeMode()"
						[palette]="effectivePalette()"
						[themeConfig]="themeConfig()"
					/>
				}

				@if (isColumnChart() && (hasValidData() || allowEmptyChart())) {
					<jepto-column-chart
						[cardId]="
							effectiveConfig()?.cardId
						"
						[config]="preparedConfig()"
						[data]="effectiveData()"
						[themeMode]="themeMode()"
						[palette]="effectivePalette()"
						[themeConfig]="themeConfig()"
					/>
				}

				@if (isDonutChart() && (hasValidData() || allowEmptyChart())) {
					<jepto-donut-chart
						[cardId]="
							effectiveConfig()?.cardId
						"
						[config]="preparedConfig()"
						[data]="effectiveData()"
						[themeMode]="themeMode()"
						[palette]="effectivePalette()"
						[themeConfig]="themeConfig()"
					/>
				}

				@if (
					isScatterChart() && (hasValidData() || allowEmptyChart())
				) {
					<jepto-scatter-chart
						[cardId]="
							effectiveConfig()?.cardId
						"
						[config]="preparedConfig()"
						[data]="effectiveData()"
						[themeMode]="themeMode()"
						[palette]="effectivePalette()"
						[themeConfig]="themeConfig()"
					/>
				}

				@if (
					isNumberChart() &&
					(hasValidData() || allowEmptyChart())
				) {
					<jepto-number-chart
						[cardId]="
							effectiveConfig()?.cardId
						"
						[config]="preparedConfig()"
						[data]="effectiveData()"
						[themeMode]="themeMode()"
						[palette]="effectivePalette()"
					/>
				}
			}
		</div>
	}

	@if (isSelected()) {
		<div class="card-action-buttons">
			<button
				type="button"
				class="card-action-btn"
				aria-label="Edit card"
				(click)="editCard.emit()"
			>
				<span class="icon-edit-2"></span>
			</button>
			<button
				type="button"
				class="card-action-btn"
				aria-label="More options"
				[matMenuTriggerFor]="cardMenu"
				(click)="$event.stopPropagation()"
			>
				<span class="icon-more-vertical"></span>
			</button>
		</div>

		<mat-menu #cardMenu="matMenu" panelClass="card-actions-menu">
			<button mat-menu-item (click)="editCard.emit()">Edit</button>
			<button mat-menu-item (click)="duplicateCard.emit()">
				Duplicate
			</button>
			<button mat-menu-item disabled type="button">Save card</button>
			<button
				mat-menu-item
				class="card-menu-item--danger"
				(click)="deleteCard.emit()"
			>
				Delete
			</button>
		</mat-menu>
	}
</div>
